import { useState, useEffect } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const PRESET_CATEGORIES = [
  { name: "Housing",       icon: "🏠", color: "#4f8ef7" },
  { name: "Groceries",     icon: "🛒", color: "#43c59e" },
  { name: "Transport",     icon: "🚗", color: "#f7a44f" },
  { name: "Dining Out",    icon: "🍽️", color: "#f76f6f" },
  { name: "Entertainment", icon: "🎬", color: "#a78bfa" },
  { name: "Health",        icon: "💊", color: "#34d399" },
  { name: "Clothing",      icon: "👗", color: "#f472b6" },
  { name: "Savings",       icon: "💰", color: "#fbbf24" },
  { name: "Investments",   icon: "📈", color: "#60a5fa" },
  { name: "Subscriptions", icon: "📱", color: "#818cf8" },
];
const PERIOD_OPTIONS = [
  { label: "3 Months", multiplier: 3 },
  { label: "6 Months", multiplier: 6 },
  { label: "1 Year",   multiplier: 12 },
];
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];
const ICONS = ["🏠","🛒","🚗","🍽️","🎬","💊","👗","💰","📈","📱","🐾","✈️","🎓","🧾","💡","🎮","🏋️","🎁","🧴","🧘"];
const LS_SAVES    = "payallocator-saves";
const LS_CURRENT  = "payallocator-current";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(val) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency", currency: "CAD", maximumFractionDigits: 0
  }).format(val || 0);
}
function fmtMonth(key) {
  const [y, m] = key.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}
function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────
function lsGet(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch { return fallback; }
}
function lsSet(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// ─── Shared styles ─────────────────────────────────────────────────────────────
const lbl = {
  fontSize: "11px", fontWeight: 600, color: "#6b7280",
  textTransform: "uppercase", letterSpacing: "0.08em",
  display: "block", marginBottom: "6px"
};
const inp = {
  width: "100%", background: "#0b0f1a", border: "1px solid #374151",
  borderRadius: "8px", padding: "10px 12px", color: "#f3f4f6",
  fontSize: "15px", outline: "none"
};
const btnP = {
  flex: 1, background: "linear-gradient(135deg,#4f8ef7,#a78bfa)",
  border: "none", borderRadius: "8px", padding: "11px",
  color: "#fff", fontSize: "14px", fontWeight: 600, cursor: "pointer"
};
const btnS = {
  flex: 1, background: "#1f2937", border: "1px solid #374151",
  borderRadius: "8px", padding: "11px", color: "#9ca3af",
  fontSize: "14px", cursor: "pointer"
};

// ─── DonutChart ───────────────────────────────────────────────────────────────
function DonutChart({ categories, total }) {
  const size = 200, r = 80, cx = 100, cy = 100, C = 2 * Math.PI * r;
  let off = 0;
  const slices = categories.filter(c => c.amount > 0 && total > 0).map(c => {
    const dash = (c.amount / total) * C;
    const s = { ...c, dash, gap: C - dash, off };
    off += dash;
    return s;
  });
  const unalloc = Math.max(0, total - categories.reduce((s, c) => s + (c.amount || 0), 0));
  if (unalloc > 0 && total > 0) {
    const dash = (unalloc / total) * C;
    slices.push({ name: "Unallocated", color: "#2d3748", dash, gap: C - dash, off });
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.length === 0 && (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#2d3748" strokeWidth="28" />
      )}
      {slices.map((s, i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color}
          strokeWidth="28" strokeDasharray={`${s.dash} ${s.gap}`}
          strokeDashoffset={-s.off + C / 4}
          style={{ transition: "stroke-dasharray 0.5s ease" }} />
      ))}
      <circle cx={cx} cy={cy} r={56} fill="#111827" />
    </svg>
  );
}

// ─── BarChart ─────────────────────────────────────────────────────────────────
function BarChart({ categories, multiplier }) {
  const max = Math.max(...categories.map(c => (c.amount || 0) * multiplier), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {categories.filter(c => c.amount > 0).map((c, i) => {
        const val = (c.amount || 0) * multiplier;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ width: "22px", fontSize: "16px" }}>{c.icon}</span>
            <div style={{ flex: 1, background: "#1f2937", borderRadius: "6px", height: "22px", overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${(val / max) * 100}%`,
                background: c.color, borderRadius: "6px",
                transition: "width 0.6s cubic-bezier(.4,0,.2,1)", opacity: 0.85
              }} />
            </div>
            <span style={{ width: "80px", textAlign: "right", fontSize: "13px", color: "#e5e7eb", fontFamily: "monospace" }}>
              {fmt(val)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── SaveModal ────────────────────────────────────────────────────────────────
function SaveModal({ onSave, onClose, existingSaves }) {
  const [monthKey, setMonthKey] = useState(currentMonthKey());
  const [note, setNote] = useState("");
  const already = existingSaves.some(s => s.monthKey === monthKey);
  const now = new Date();
  const opts = [];
  for (let i = -12; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ k, label: fmtMonth(k) });
  }
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }} onClick={onClose}>
      <div style={{ background: "#111827", border: "1px solid #374151", borderRadius: "20px", padding: "28px", width: "420px", maxWidth: "95vw" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: "17px", marginBottom: "4px" }}>💾 Save Allocation</div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "22px" }}>Snapshot your current budget for this period.</div>
        <label style={lbl}>Period (Month)</label>
        <select value={monthKey} onChange={e => setMonthKey(e.target.value)}
          style={{ ...inp, marginBottom: already ? "4px" : "16px", cursor: "pointer" }}>
          {opts.map(o => <option key={o.k} value={o.k}>{o.label}</option>)}
        </select>
        {already && (
          <div style={{ fontSize: "12px", color: "#f7a44f", marginBottom: "14px" }}>
            ⚠️ A save already exists for this month — saving will overwrite it.
          </div>
        )}
        <label style={lbl}>Note <span style={{ color: "#4b5563", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
        <input value={note} onChange={e => setNote(e.target.value)}
          placeholder='"Got a raise", "Post-vacation reset"'
          style={{ ...inp, marginBottom: "22px" }} />
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onClose} style={btnS}>Cancel</button>
          <button onClick={() => onSave(monthKey, note)} style={btnP}>
            {already ? "Overwrite" : "Save Allocation"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HistoryPanel ─────────────────────────────────────────────────────────────
function HistoryPanel({ saves, onLoad, onDelete, onClose, activeMonthKey }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const sorted = [...saves].sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "flex-start", justifyContent: "flex-end", zIndex: 200 }} onClick={onClose}>
      <div style={{ background: "#111827", borderLeft: "1px solid #1f2937", width: "420px", maxWidth: "95vw", height: "100vh", overflowY: "auto", padding: "28px 22px", animation: "slideIn .22s ease" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "17px" }}>📅 Allocation History</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
              {saves.length} saved period{saves.length !== 1 ? "s" : ""}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "#1f2937", border: "1px solid #374151", borderRadius: "8px", padding: "6px 12px", color: "#9ca3af", cursor: "pointer", fontSize: "18px" }}>×</button>
        </div>
        {sorted.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#4b5563" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🗂️</div>
            <div style={{ fontSize: "14px" }}>No saved allocations yet.</div>
            <div style={{ fontSize: "12px", marginTop: "4px" }}>Hit "Save" to snapshot your current budget.</div>
          </div>
        )}
        {sorted.map(s => {
          const tot = s.categories.reduce((a, c) => a + (c.amount || 0), 0);
          const sv  = s.categories.filter(c => ["Savings","Investments"].includes(c.name)).reduce((a, c) => a + (c.amount || 0), 0);
          const isActive = s.monthKey === activeMonthKey;
          return (
            <div key={s.monthKey} style={{ background: "#0b0f1a", border: `1px solid ${isActive ? "#4f8ef7" : "#1f2937"}`, borderRadius: "14px", padding: "18px", marginBottom: "12px", position: "relative" }}>
              {isActive && (
                <div style={{ position: "absolute", top: "12px", right: "12px", fontSize: "10px", fontWeight: 700, color: "#4f8ef7", background: "#1e3a5f", padding: "2px 8px", borderRadius: "99px", letterSpacing: "0.08em" }}>LOADED</div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px" }}>{fmtMonth(s.monthKey)}</div>
                  {s.note && <div style={{ fontSize: "12px", color: "#a78bfa", marginTop: "2px", fontStyle: "italic" }}>"{s.note}"</div>}
                  <div style={{ fontSize: "11px", color: "#4b5563", marginTop: "3px" }}>
                    Saved {new Date(s.savedAt).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => { onLoad(s); onClose(); }} style={{ background: "#1f2937", border: "1px solid #374151", borderRadius: "7px", padding: "5px 12px", color: "#4f8ef7", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>Load</button>
                  <button onClick={() => setConfirmDel(s.monthKey)} style={{ background: "#1f2937", border: "1px solid #374151", borderRadius: "7px", padding: "5px 10px", color: "#6b7280", fontSize: "13px", cursor: "pointer" }}>🗑</button>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "12px" }}>
                {[
                  { label: "Income",    val: s.paychequePerMonth, color: "#4f8ef7" },
                  { label: "Allocated", val: tot,                 color: "#f76f6f" },
                  { label: "Savings",   val: sv,                  color: "#43c59e" },
                ].map((x, j) => (
                  <div key={j} style={{ background: "#111827", borderRadius: "8px", padding: "8px 10px" }}>
                    <div style={{ fontSize: "10px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "2px" }}>{x.label}</div>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: x.color, fontFamily: "monospace" }}>{fmt(x.val)}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {s.categories.filter(c => c.amount > 0).sort((a, b) => b.amount - a.amount).slice(0, 5).map((c, j) => (
                  <div key={j} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#9ca3af" }}>
                    <span>{c.icon} {c.name}</span>
                    <span style={{ fontFamily: "monospace", color: "#d1d5db" }}>{fmt(c.amount)}</span>
                  </div>
                ))}
                {s.categories.filter(c => c.amount > 0).length > 5 && (
                  <div style={{ fontSize: "11px", color: "#4b5563" }}>+{s.categories.filter(c => c.amount > 0).length - 5} more</div>
                )}
              </div>
              {confirmDel === s.monthKey && (
                <div style={{ marginTop: "12px", background: "#1f1010", border: "1px solid #7f1d1d", borderRadius: "8px", padding: "10px 12px" }}>
                  <div style={{ fontSize: "13px", color: "#fca5a5", marginBottom: "8px" }}>Delete this save? This can't be undone.</div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => setConfirmDel(null)} style={{ flex: 1, background: "#1f2937", border: "1px solid #374151", borderRadius: "6px", padding: "6px", color: "#9ca3af", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
                    <button onClick={() => { onDelete(s.monthKey); setConfirmDel(null); }} style={{ flex: 1, background: "#7f1d1d", border: "none", borderRadius: "6px", padding: "6px", color: "#fca5a5", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ComparePanel ─────────────────────────────────────────────────────────────
function ComparePanel({ saves, currentCategories, currentIncome, onClose }) {
  const sorted = [...saves].sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  const allNames = Array.from(new Set([
    ...sorted.flatMap(s => s.categories.map(c => c.name)),
    ...currentCategories.map(c => c.name),
  ]));
  const cols = [
    ...sorted,
    { monthKey: "current", categories: currentCategories, paychequePerMonth: currentIncome },
  ];
  const getAmt  = (col, name) => col.categories.find(c => c.name === name)?.amount || 0;
  const getIcon = name => {
    const p = PRESET_CATEGORIES.find(p => p.name === name);
    if (p) return p.icon;
    for (const col of cols) { const c = col.categories.find(c => c.name === name); if (c) return c.icon; }
    return "💡";
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "20px" }} onClick={onClose}>
      <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "20px", width: "100%", maxWidth: "960px", maxHeight: "88vh", overflow: "auto", padding: "28px" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "22px" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "17px" }}>📊 Period Comparison</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>▲▼ changes vs previous period shown in colour</div>
          </div>
          <button onClick={onClose} style={{ background: "#1f2937", border: "1px solid #374151", borderRadius: "8px", padding: "6px 14px", color: "#9ca3af", cursor: "pointer", fontSize: "15px" }}>×</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", minWidth: "520px" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", color: "#6b7280", fontWeight: 600, paddingBottom: "10px", borderBottom: "1px solid #1f2937", paddingRight: "20px" }}>Category</th>
                {cols.map(c => (
                  <th key={c.monthKey} style={{ textAlign: "right", color: c.monthKey === "current" ? "#a78bfa" : "#9ca3af", fontWeight: 600, paddingBottom: "10px", borderBottom: "1px solid #1f2937", paddingLeft: "16px", whiteSpace: "nowrap" }}>
                    {c.monthKey === "current" ? "● Current" : fmtMonth(c.monthKey)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: "#0b0f1a" }}>
                <td style={{ padding: "9px 20px 9px 0", fontWeight: 600, color: "#4f8ef7" }}>💵 Income</td>
                {cols.map(c => (
                  <td key={c.monthKey} style={{ textAlign: "right", padding: "9px 0 9px 16px", fontFamily: "monospace", fontWeight: 700, color: "#4f8ef7" }}>{fmt(c.paychequePerMonth)}</td>
                ))}
              </tr>
              {allNames.map(name => (
                <tr key={name} style={{ borderBottom: "1px solid #1f2937" }}>
                  <td style={{ padding: "9px 20px 9px 0", color: "#d1d5db" }}>{getIcon(name)} {name}</td>
                  {cols.map((c, ci) => {
                    const amt  = getAmt(c, name);
                    const prev = cols[ci - 1];
                    const diff = prev ? amt - getAmt(prev, name) : null;
                    return (
                      <td key={c.monthKey} style={{ textAlign: "right", padding: "9px 0 9px 16px", fontFamily: "monospace", color: amt === 0 ? "#374151" : "#e5e7eb" }}>
                        {amt > 0 ? fmt(amt) : "—"}
                        {diff !== null && diff !== 0 && (
                          <span style={{ fontSize: "11px", marginLeft: "5px", color: diff > 0 ? "#f76f6f" : "#43c59e" }}>
                            {diff > 0 ? "▲" : "▼"}{fmt(Math.abs(diff))}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr style={{ background: "#0b0f1a" }}>
                <td style={{ padding: "9px 20px 9px 0", fontWeight: 600, color: "#43c59e" }}>💰 Savings + Investments</td>
                {cols.map(c => {
                  const sv = c.categories.filter(x => ["Savings","Investments"].includes(x.name)).reduce((s, x) => s + (x.amount || 0), 0);
                  return <td key={c.monthKey} style={{ textAlign: "right", padding: "9px 0 9px 16px", fontFamily: "monospace", fontWeight: 700, color: "#43c59e" }}>{fmt(sv)}</td>;
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
const DEFAULT_CATEGORIES = [
  { ...PRESET_CATEGORIES[0], amount: 1500 },
  { ...PRESET_CATEGORIES[1], amount: 500  },
  { ...PRESET_CATEGORIES[2], amount: 300  },
  { ...PRESET_CATEGORIES[7], amount: 600  },
  { ...PRESET_CATEGORIES[8], amount: 400  },
];

export default function App() {
  // ── State – hydrated from localStorage on first render ──────────────────────
  const [paycheque,  setPaychequeRaw]  = useState(() => lsGet(LS_CURRENT + "-paycheque",  5000));
  const [payFreq,    setPayFreqRaw]    = useState(() => lsGet(LS_CURRENT + "-payfreq",    "bi-weekly"));
  const [categories, setCategoriesRaw] = useState(() => lsGet(LS_CURRENT + "-categories", DEFAULT_CATEGORIES));
  const [saves,      setSavesRaw]      = useState(() => lsGet(LS_SAVES,                   []));
  const [activeKey,  setActiveKey]     = useState(() => lsGet(LS_CURRENT + "-activekey",  null));

  const [period,      setPeriod]      = useState(0);
  const [tab,         setTab]         = useState("allocate");
  const [showAdd,     setShowAdd]     = useState(false);
  const [newCat,      setNewCat]      = useState({ name: "", icon: "💡", color: "#a78bfa", amount: 0 });
  const [showSave,    setShowSave]    = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [toast,       setToast]       = useState(null);

  // ── Setters that also persist to localStorage ───────────────────────────────
  function setPaycheque(v)  { setPaychequeRaw(v);  lsSet(LS_CURRENT + "-paycheque",  v); }
  function setPayFreq(v)    { setPayFreqRaw(v);    lsSet(LS_CURRENT + "-payfreq",    v); }
  function setCategories(fn) {
    setCategoriesRaw(prev => {
      const next = typeof fn === "function" ? fn(prev) : fn;
      lsSet(LS_CURRENT + "-categories", next);
      return next;
    });
  }
  function setSaves(fn) {
    setSavesRaw(prev => {
      const next = typeof fn === "function" ? fn(prev) : fn;
      lsSet(LS_SAVES, next);
      return next;
    });
  }
  function setActiveKeyP(v) { setActiveKey(v); lsSet(LS_CURRENT + "-activekey", v); }

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  // ── Derived values ───────────────────────────────────────────────────────────
  const paychequePerMonth =
    payFreq === "weekly"        ? paycheque * 52 / 12 :
    payFreq === "bi-weekly"     ? paycheque * 26 / 12 :
    payFreq === "semi-monthly"  ? paycheque * 24 / 12 : paycheque;

  const totalAllocated = categories.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const unallocated    = paychequePerMonth - totalAllocated;
  const allocPct       = Math.min(100, (totalAllocated / paychequePerMonth) * 100);
  const multiplier     = PERIOD_OPTIONS[period].multiplier;
  const savingsTotal   = categories
    .filter(c => ["Savings","Investments"].includes(c.name))
    .reduce((s, c) => s + (c.amount || 0), 0);

  // ── Category helpers ─────────────────────────────────────────────────────────
  function updateCat(i, f, v) { setCategories(p => p.map((c, j) => j === i ? { ...c, [f]: v } : c)); }
  function removeCat(i)       { setCategories(p => p.filter((_, j) => j !== i)); }
  function addPreset(p)       { if (!categories.find(c => c.name === p.name)) setCategories(prev => [...prev, { ...p, amount: 0 }]); }
  function addCustomCat() {
    if (!newCat.name.trim()) return;
    setCategories(p => [...p, { ...newCat, amount: Number(newCat.amount) || 0 }]);
    setNewCat({ name: "", icon: "💡", color: "#a78bfa", amount: 0 });
    setShowAdd(false);
  }

  // ── Save / load / delete ─────────────────────────────────────────────────────
  function handleSave(monthKey, note) {
    const snap = {
      monthKey, note, paycheque, payFreq, paychequePerMonth,
      categories: categories.map(c => ({ ...c })),
      savedAt: new Date().toISOString(),
    };
    setSaves(prev => [...prev.filter(s => s.monthKey !== monthKey), snap]);
    setActiveKeyP(monthKey);
    setShowSave(false);
    showToast(`Saved — ${fmtMonth(monthKey)}`);
  }
  function handleLoad(save) {
    setPaycheque(save.paycheque);
    setPayFreq(save.payFreq || "bi-weekly");
    setCategories(save.categories.map(c => ({ ...c })));
    setActiveKeyP(save.monthKey);
    showToast(`Loaded — ${fmtMonth(save.monthKey)}`, "info");
  }
  function handleDelete(monthKey) {
    setSaves(prev => prev.filter(s => s.monthKey !== monthKey));
    if (activeKey === monthKey) setActiveKeyP(null);
    showToast("Save deleted", "warn");
  }

  // ── Period navigator ─────────────────────────────────────────────────────────
  const sortedSaves = [...saves].sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  const activeIdx   = sortedSaves.findIndex(s => s.monthKey === activeKey);
  function navSave(dir) { const n = sortedSaves[activeIdx + dir]; if (n) handleLoad(n); }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#0b0f1a", color: "#f3f4f6", fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box}
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}
        ::-webkit-scrollbar{width:6px}
        ::-webkit-scrollbar-track{background:#111827}
        ::-webkit-scrollbar-thumb{background:#374151;border-radius:3px}
        .crow:hover{background:#1f2937!important}
        .hbtn{border:none;cursor:pointer;transition:all .18s}
        .hbtn:hover{opacity:.85;transform:translateY(-1px)}
        .ipk:hover{background:#374151!important;transform:scale(1.15)}
        .pchip:hover{background:#1f2937!important;border-color:#4f8ef7!important}
        .rmbtn{opacity:0;transition:opacity .15s}
        .crow:hover .rmbtn{opacity:1}
        @keyframes slideIn{from{transform:translateX(60px);opacity:0}to{transform:none;opacity:1}}
        @keyframes fadeUp{from{opacity:0;transform:translateX(-50%) translateY(10px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: "24px", left: "50%", transform: "translateX(-50%)", zIndex: 999,
          background: toast.type === "warn" ? "#7f1d1d" : toast.type === "info" ? "#1e3a5f" : "#14532d",
          border: `1px solid ${toast.type === "warn" ? "#ef4444" : toast.type === "info" ? "#4f8ef7" : "#22c55e"}`,
          borderRadius: "10px", padding: "10px 22px", fontSize: "14px", fontWeight: 500,
          color: "#f3f4f6", boxShadow: "0 8px 32px rgba(0,0,0,.5)", whiteSpace: "nowrap",
          animation: "fadeUp .25s ease",
        }}>
          {toast.msg}
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────────────── */}
      <div style={{ background: "#111827", borderBottom: "1px solid #1f2937", padding: "15px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "linear-gradient(135deg,#4f8ef7,#a78bfa)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>💸</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "17px", letterSpacing: "-0.3px" }}>PayAllocator</div>
            <div style={{ fontSize: "11px", color: "#6b7280" }}>Personal Finance Planner</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "6px" }}>
          {["allocate","projection","insights"].map(t => (
            <button key={t} className="hbtn" onClick={() => setTab(t)} style={{
              padding: "7px 15px", borderRadius: "8px", fontSize: "13px", fontWeight: 500,
              background: tab === t ? "#1f2937" : "transparent",
              color: tab === t ? "#f3f4f6" : "#9ca3af",
              border: tab === t ? "1px solid #374151" : "1px solid transparent",
            }}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {saves.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px", background: "#1f2937", border: "1px solid #374151", borderRadius: "9px", padding: "4px" }}>
              <button className="hbtn" onClick={() => navSave(1)} disabled={activeIdx >= sortedSaves.length - 1}
                style={{ background: "none", border: "none", color: activeIdx >= sortedSaves.length - 1 ? "#374151" : "#9ca3af", fontSize: "16px", padding: "2px 8px", borderRadius: "6px", cursor: activeIdx >= sortedSaves.length - 1 ? "default" : "pointer", lineHeight: 1 }}>‹</button>
              <span style={{ fontSize: "12px", color: activeKey ? "#d1d5db" : "#6b7280", minWidth: "96px", textAlign: "center", fontWeight: 500 }}>
                {activeKey ? fmtMonth(activeKey) : "Unsaved"}
              </span>
              <button className="hbtn" onClick={() => navSave(-1)} disabled={activeIdx <= 0 || activeIdx === -1}
                style={{ background: "none", border: "none", color: activeIdx <= 0 || activeIdx === -1 ? "#374151" : "#9ca3af", fontSize: "16px", padding: "2px 8px", borderRadius: "6px", cursor: activeIdx <= 0 || activeIdx === -1 ? "default" : "pointer", lineHeight: 1 }}>›</button>
            </div>
          )}
          {saves.length > 1 && (
            <button className="hbtn" onClick={() => setShowCompare(true)} style={{ background: "#1f2937", border: "1px solid #374151", borderRadius: "8px", padding: "7px 13px", color: "#9ca3af", fontSize: "13px" }}>📊 Compare</button>
          )}
          <button className="hbtn" onClick={() => setShowHistory(true)} style={{ background: "#1f2937", border: "1px solid #374151", borderRadius: "8px", padding: "7px 13px", color: "#9ca3af", fontSize: "13px" }}>
            📅 History {saves.length > 0 && <span style={{ marginLeft: "4px", background: "#374151", borderRadius: "99px", padding: "1px 6px", fontSize: "11px", color: "#d1d5db" }}>{saves.length}</span>}
          </button>
          <button className="hbtn" onClick={() => setShowSave(true)} style={{ background: "linear-gradient(135deg,#4f8ef7,#a78bfa)", border: "none", borderRadius: "8px", padding: "7px 16px", color: "#fff", fontSize: "13px", fontWeight: 600 }}>💾 Save</button>
        </div>
      </div>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "26px 20px" }}>

        {/* ── Paycheque strip ──────────────────────────────────────────────────── */}
        <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px", marginBottom: "20px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", alignItems: "flex-end" }}>
            <div style={{ flex: "1", minWidth: "170px" }}>
              <label style={lbl}>Paycheque Amount</label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#0b0f1a", border: "1px solid #374151", borderRadius: "10px", padding: "10px 14px" }}>
                <span style={{ color: "#6b7280" }}>$</span>
                <input type="number" value={paycheque} onChange={e => setPaycheque(Number(e.target.value))}
                  style={{ background: "none", border: "none", outline: "none", color: "#f3f4f6", fontSize: "22px", fontWeight: 600, width: "100%", fontFamily: "DM Mono,monospace" }} />
              </div>
            </div>
            <div style={{ flex: "1", minWidth: "170px" }}>
              <label style={lbl}>Pay Frequency</label>
              <select value={payFreq} onChange={e => setPayFreq(e.target.value)}
                style={{ width: "100%", background: "#0b0f1a", border: "1px solid #374151", borderRadius: "10px", padding: "11px 14px", color: "#f3f4f6", fontSize: "14px", outline: "none", cursor: "pointer" }}>
                <option value="weekly">Weekly</option>
                <option value="bi-weekly">Bi-Weekly</option>
                <option value="semi-monthly">Semi-Monthly (2×/month)</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div style={{ flex: "1", minWidth: "140px", background: "#0b0f1a", borderRadius: "10px", border: "1px solid #374151", padding: "10px 16px" }}>
              <label style={lbl}>Monthly Income</label>
              <div style={{ fontSize: "22px", fontWeight: 700, color: "#4f8ef7", fontFamily: "DM Mono,monospace" }}>{fmt(paychequePerMonth)}</div>
            </div>
            <div style={{ flex: "1", minWidth: "140px", background: "#0b0f1a", borderRadius: "10px", border: "1px solid #374151", padding: "10px 16px" }}>
              <label style={lbl}>Unallocated</label>
              <div style={{ fontSize: "22px", fontWeight: 700, color: unallocated >= 0 ? "#43c59e" : "#f76f6f", fontFamily: "DM Mono,monospace" }}>{fmt(unallocated)}</div>
            </div>
          </div>
          <div style={{ marginTop: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#6b7280", marginBottom: "5px" }}>
              <span>Allocated: {fmt(totalAllocated)}</span>
              <span>{Math.round(allocPct)}% of income</span>
            </div>
            <div style={{ height: "7px", background: "#1f2937", borderRadius: "99px", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${allocPct}%`, background: allocPct > 100 ? "#f76f6f" : allocPct > 90 ? "#fbbf24" : "linear-gradient(90deg,#4f8ef7,#a78bfa)", borderRadius: "99px", transition: "width 0.5s ease" }} />
            </div>
          </div>
        </div>

        {/* ── ALLOCATE ────────────────────────────────────────────────────────── */}
        {tab === "allocate" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "18px" }}>
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
                <div style={{ fontWeight: 700, fontSize: "15px" }}>Categories</div>
                <button className="hbtn" onClick={() => setShowAdd(true)} style={{ background: "linear-gradient(135deg,#4f8ef7,#a78bfa)", color: "#fff", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", fontWeight: 600, border: "none" }}>+ Add</button>
              </div>
              {categories.map((c, i) => (
                <div key={i} className="crow" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 10px", borderRadius: "10px", marginBottom: "4px", background: "transparent", transition: "background .15s" }}>
                  <span style={{ fontSize: "19px", width: "26px", textAlign: "center" }}>{c.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "13px", fontWeight: 500, color: "#d1d5db", marginBottom: "5px" }}>{c.name}</div>
                    <input type="range" min={0} max={paychequePerMonth} step={10} value={c.amount || 0}
                      onChange={e => updateCat(i, "amount", Number(e.target.value))}
                      style={{ width: "100%", accentColor: c.color }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "3px", background: "#0b0f1a", border: "1px solid #374151", borderRadius: "7px", padding: "5px 9px", minWidth: "100px" }}>
                    <span style={{ color: "#6b7280", fontSize: "12px" }}>$</span>
                    <input type="number" value={c.amount || ""} onChange={e => updateCat(i, "amount", Number(e.target.value))}
                      style={{ background: "none", border: "none", outline: "none", color: "#f3f4f6", fontSize: "14px", fontWeight: 600, width: "60px", fontFamily: "DM Mono,monospace" }} />
                  </div>
                  <span style={{ fontSize: "11px", color: "#6b7280", minWidth: "30px", textAlign: "right" }}>
                    {paychequePerMonth > 0 ? Math.round((c.amount / paychequePerMonth) * 100) : 0}%
                  </span>
                  <button className="rmbtn" onClick={() => removeCat(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "16px", padding: "2px 4px" }}>×</button>
                </div>
              ))}
              <div style={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid #1f2937" }}>
                <div style={{ ...lbl, marginBottom: "10px" }}>Quick Add Presets</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
                  {PRESET_CATEGORIES.filter(p => !categories.find(c => c.name === p.name)).map((p, i) => (
                    <button key={i} className="pchip" onClick={() => addPreset(p)}
                      style={{ background: "#111827", border: "1px solid #374151", borderRadius: "20px", padding: "4px 11px", fontSize: "12px", color: "#9ca3af", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                      {p.icon} {p.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "14px", width: "100%" }}>Allocation</div>
              <DonutChart categories={categories} total={paychequePerMonth} />
              <div style={{ width: "100%", marginTop: "14px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {categories.filter(c => c.amount > 0).map((c, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div style={{ width: "7px", height: "7px", borderRadius: "2px", background: c.color }} />
                      <span style={{ color: "#9ca3af" }}>{c.icon} {c.name}</span>
                    </div>
                    <span style={{ fontFamily: "DM Mono,monospace", color: "#d1d5db" }}>{fmt(c.amount)}</span>
                  </div>
                ))}
                {unallocated > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div style={{ width: "7px", height: "7px", borderRadius: "2px", background: "#2d3748" }} />
                      <span style={{ color: "#6b7280" }}>Unallocated</span>
                    </div>
                    <span style={{ fontFamily: "DM Mono,monospace", color: "#6b7280" }}>{fmt(unallocated)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── PROJECTION ──────────────────────────────────────────────────────── */}
        {tab === "projection" && (
          <div>
            <div style={{ display: "flex", gap: "10px", marginBottom: "18px" }}>
              {PERIOD_OPTIONS.map((p, i) => (
                <button key={i} className="hbtn" onClick={() => setPeriod(i)} style={{ padding: "8px 22px", borderRadius: "10px", fontSize: "14px", fontWeight: 600, background: period === i ? "linear-gradient(135deg,#4f8ef7,#a78bfa)" : "#111827", color: period === i ? "#fff" : "#9ca3af", border: period === i ? "none" : "1px solid #374151" }}>{p.label}</button>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px", marginBottom: "18px" }}>
              {[
                { label: "Total Income",   value: paychequePerMonth * multiplier, color: "#4f8ef7" },
                { label: "Total Spending", value: totalAllocated * multiplier,    color: "#f76f6f" },
                { label: "Net Savings",    value: savingsTotal * multiplier,      color: "#43c59e" },
              ].map((s, i) => (
                <div key={i} style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "14px", padding: "18px" }}>
                  <label style={lbl}>{s.label}</label>
                  <div style={{ fontSize: "26px", fontWeight: 700, fontFamily: "DM Mono,monospace", color: s.color }}>{fmt(s.value)}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "3px" }}>over {PERIOD_OPTIONS[period].label.toLowerCase()}</div>
                </div>
              ))}
            </div>
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px", marginBottom: "18px" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "18px" }}>Category Breakdown — {PERIOD_OPTIONS[period].label}</div>
              <BarChart categories={categories} multiplier={multiplier} />
            </div>
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "18px" }}>All-Period Comparison</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", color: "#6b7280", fontWeight: 600, paddingBottom: "10px", borderBottom: "1px solid #1f2937" }}>Category</th>
                    {PERIOD_OPTIONS.map(p => <th key={p.label} style={{ textAlign: "right", color: "#6b7280", fontWeight: 600, paddingBottom: "10px", borderBottom: "1px solid #1f2937", paddingLeft: "16px" }}>{p.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {categories.filter(c => c.amount > 0).map((c, i) => (
                    <tr key={i}>
                      <td style={{ padding: "9px 0", borderBottom: "1px solid #1f2937", color: "#d1d5db" }}>{c.icon} {c.name}</td>
                      {PERIOD_OPTIONS.map(p => <td key={p.label} style={{ textAlign: "right", padding: "9px 0 9px 16px", borderBottom: "1px solid #1f2937", fontFamily: "DM Mono,monospace", color: "#e5e7eb" }}>{fmt(c.amount * p.multiplier)}</td>)}
                    </tr>
                  ))}
                  <tr>
                    <td style={{ padding: "11px 0", color: "#fbbf24", fontWeight: 600 }}>💰 Total Savings</td>
                    {PERIOD_OPTIONS.map(p => <td key={p.label} style={{ textAlign: "right", padding: "11px 0 11px 16px", fontFamily: "DM Mono,monospace", color: "#43c59e", fontWeight: 700 }}>{fmt(savingsTotal * p.multiplier)}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── INSIGHTS ────────────────────────────────────────────────────────── */}
        {tab === "insights" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "4px" }}>50/30/20 Rule Check</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "18px" }}>50% needs · 30% wants · 20% savings</div>
              {(() => {
                const needs = categories.filter(c => ["Housing","Groceries","Transport","Health"].includes(c.name)).reduce((s,c) => s+c.amount, 0);
                const sv    = categories.filter(c => ["Savings","Investments"].includes(c.name)).reduce((s,c) => s+c.amount, 0);
                const wants = totalAllocated - needs - sv;
                const b     = paychequePerMonth;
                return [
                  { label: "Needs",   val: needs, target: b * .5, color: "#4f8ef7", tip: "Housing, Groceries, Transport, Health" },
                  { label: "Wants",   val: wants, target: b * .3, color: "#a78bfa", tip: "Dining, Entertainment, Clothing…" },
                  { label: "Savings", val: sv,    target: b * .2, color: "#43c59e", tip: "Savings & Investments" },
                ].map((r, i) => {
                  const pct  = b > 0 ? Math.round((r.val / b) * 100) : 0;
                  const tPct = b > 0 ? Math.round((r.target / b) * 100) : 0;
                  const ok   = r.label === "Savings" ? pct >= tPct : pct <= tPct;
                  return (
                    <div key={i} style={{ marginBottom: "14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 600 }}>{r.label}</span>
                        <span style={{ fontSize: "12px", fontFamily: "DM Mono,monospace" }}>
                          <span style={{ color: r.color }}>{pct}%</span>
                          <span style={{ color: "#6b7280" }}> / {tPct}% target</span>
                          <span style={{ marginLeft: "5px" }}>{ok ? "✅" : "⚠️"}</span>
                        </span>
                      </div>
                      <div style={{ height: "7px", background: "#1f2937", borderRadius: "99px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: r.color, borderRadius: "99px" }} />
                      </div>
                      <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>{r.tip}</div>
                    </div>
                  );
                });
              })()}
            </div>
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "4px" }}>Savings Milestones</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "18px" }}>At {fmt(savingsTotal)}/mo saved</div>
              {[1000, 5000, 10000, 25000, 50000, 100000].map((m, i) => {
                const months = savingsTotal > 0 ? m / savingsTotal : Infinity;
                return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #1f2937" }}>
                    <div style={{ fontFamily: "DM Mono,monospace", fontWeight: 600, color: "#fbbf24", fontSize: "13px" }}>{fmt(m)}</div>
                    <div style={{ fontSize: "13px", color: "#9ca3af" }}>
                      {savingsTotal > 0 ? months < 1 ? "< 1 month" : months < 24 ? `${Math.ceil(months)} months` : `${(months/12).toFixed(1)} years` : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "16px" }}>Top Spending Categories</div>
              {[...categories].sort((a,b) => b.amount - a.amount).slice(0, 5).map((c, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: c.color + "33", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>{c.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "13px", fontWeight: 500 }}>{c.name}</div>
                    <div style={{ height: "4px", background: "#1f2937", borderRadius: "99px", marginTop: "4px", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${paychequePerMonth > 0 ? (c.amount / paychequePerMonth) * 100 : 0}%`, background: c.color, borderRadius: "99px" }} />
                    </div>
                  </div>
                  <span style={{ fontFamily: "DM Mono,monospace", fontSize: "13px", color: "#d1d5db" }}>{fmt(c.amount)}</span>
                </div>
              ))}
            </div>
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: "16px", padding: "22px" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "14px" }}>💡 Smart Tips</div>
              {(() => {
                const tips = [];
                const svPct = paychequePerMonth > 0 ? savingsTotal / paychequePerMonth : 0;
                if (svPct < 0.1)  tips.push({ icon: "⚠️", text: "You're saving less than 10% of income. Try to reach at least 20%.", color: "#f76f6f" });
                if (svPct >= 0.2) tips.push({ icon: "🏆", text: "Great! You're saving 20%+ of your income.", color: "#43c59e" });
                if (unallocated > paychequePerMonth * 0.15) tips.push({ icon: "💡", text: `${fmt(unallocated)} is unallocated — consider routing it to savings or investments.`, color: "#fbbf24" });
                if (unallocated < 0) tips.push({ icon: "🚨", text: "You've over-allocated your budget! Reduce some categories.", color: "#f76f6f" });
                const housing = categories.find(c => c.name === "Housing");
                if (housing && paychequePerMonth > 0 && housing.amount / paychequePerMonth > 0.35) tips.push({ icon: "🏠", text: "Housing exceeds 35% of income — a common financial stress point.", color: "#f7a44f" });
                if (tips.length === 0) tips.push({ icon: "✅", text: "Your budget looks healthy! Keep it up.", color: "#43c59e" });
                return tips.map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: "10px", marginBottom: "10px", background: t.color + "11", borderRadius: "10px", padding: "11px", border: `1px solid ${t.color}33` }}>
                    <span style={{ fontSize: "16px" }}>{t.icon}</span>
                    <span style={{ fontSize: "13px", color: "#d1d5db", lineHeight: 1.5 }}>{t.text}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      {showSave    && <SaveModal    onSave={handleSave} onClose={() => setShowSave(false)} existingSaves={saves} />}
      {showHistory && <HistoryPanel saves={saves} onLoad={handleLoad} onDelete={handleDelete} onClose={() => setShowHistory(false)} activeMonthKey={activeKey} />}
      {showCompare && <ComparePanel saves={saves} currentCategories={categories} currentIncome={paychequePerMonth} onClose={() => setShowCompare(false)} />}

      {/* ── Add Category Modal ───────────────────────────────────────────────── */}
      {showAdd && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }} onClick={() => setShowAdd(false)}>
          <div style={{ background: "#111827", border: "1px solid #374151", borderRadius: "20px", padding: "28px", width: "400px", maxWidth: "95vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: "17px", marginBottom: "18px" }}>New Category</div>
            <label style={lbl}>Name</label>
            <input value={newCat.name} onChange={e => setNewCat(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Pet Care" style={{ ...inp, marginBottom: "14px" }} />
            <label style={lbl}>Icon</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: "14px" }}>
              {ICONS.map((ic, i) => <button key={i} className="ipk" onClick={() => setNewCat(p => ({ ...p, icon: ic }))} style={{ background: newCat.icon === ic ? "#374151" : "#1f2937", border: newCat.icon === ic ? "2px solid #4f8ef7" : "2px solid transparent", borderRadius: "6px", padding: "4px", fontSize: "17px", cursor: "pointer", transition: "all .15s" }}>{ic}</button>)}
            </div>
            <label style={lbl}>Color</label>
            <div style={{ display: "flex", gap: "7px", alignItems: "center", marginBottom: "14px" }}>
              {["#4f8ef7","#43c59e","#f7a44f","#f76f6f","#a78bfa","#fbbf24","#f472b6","#34d399"].map(col => (
                <div key={col} onClick={() => setNewCat(p => ({ ...p, color: col }))} style={{ width: "22px", height: "22px", borderRadius: "5px", background: col, cursor: "pointer", border: newCat.color === col ? "3px solid #fff" : "3px solid transparent" }} />
              ))}
              <input type="color" value={newCat.color} onChange={e => setNewCat(p => ({ ...p, color: e.target.value }))} style={{ width: "26px", height: "26px", borderRadius: "5px", border: "none", background: "none", cursor: "pointer" }} />
            </div>
            <label style={lbl}>Monthly Amount</label>
            <input type="number" value={newCat.amount || ""} onChange={e => setNewCat(p => ({ ...p, amount: Number(e.target.value) }))} placeholder="0" style={{ ...inp, fontFamily: "DM Mono,monospace", marginBottom: "20px" }} />
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => setShowAdd(false)} style={btnS}>Cancel</button>
              <button onClick={addCustomCat} style={btnP}>Add Category</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
